import React, { useState } from 'react'
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Checkbox,
    FormControlLabel,
    Box,
    CircularProgress,
} from '@mui/material'
import { useTranslation } from 'react-i18next'

interface ExportColumnsPopupProps {
    open: boolean
    onClose: () => void
    columns: { id: string; name: string; selected: boolean }[]
    onExport: (selectedColumns: string[]) => void
}

const ExportColumnsPopup: React.FC<ExportColumnsPopupProps> = ({
    open,
    onClose,
    columns,
    onExport,
}) => {
    const { t } = useTranslation()
    const [selected, setSelected] = useState<Record<string, boolean>>(() =>
        columns.reduce(
            (acc, col) => ({ ...acc, [col.id]: col.selected }),
            {} as Record<string, boolean>,
        ),
    )
    const [loading, setLoading] = useState(false)

    const handleToggle = (id: string) => {
        setSelected((prev) => ({ ...prev, [id]: !prev[id] }))
    }

    const handleExport = () => {
        setLoading(true)
        try {
            const selectedColumns = columns
                .filter((col) => selected[col.id])
                .map((col) => col.id)
            onExport(selectedColumns)
        } finally {
            setLoading(false)
            onClose()
        }
    }

    const handleCancel = () => {
        setLoading(false)
        onClose()
    }

    return (
        <Dialog
            open={open}
            onClose={handleCancel}
            maxWidth={false}
            PaperProps={{
                sx: { minWidth: 600 },
            }}
        >
            <DialogTitle>{t('selectColumnsToExport')}</DialogTitle>
            <DialogContent>
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(3, 1fr)',
                        gap: 1,
                    }}
                >
                    {columns.map((col) => (
                        <FormControlLabel
                            key={col.id}
                            control={
                                <Checkbox
                                    checked={!!selected[col.id]}
                                    onChange={() => handleToggle(col.id)}
                                />
                            }
                            label={col.name}
                        />
                    ))}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleCancel} disabled={loading}>
                    {t('cancel')}
                </Button>
                <Button
                    onClick={handleExport}
                    variant="contained"
                    disabled={loading || !Object.values(selected).some(Boolean)}
                >
                    {loading ? <CircularProgress size={20} /> : t('export')}
                </Button>
            </DialogActions>
        </Dialog>
    )
}

export default ExportColumnsPopup
